import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import PartialTable from 'components/partials/PartialTable';
import { formatDateddMMyyyy } from 'helpers/dateHelper';
import { getDepartmentSpendingTransactions } from 'services/department-spending/departmentSpendingService';
import { formatCurrency } from './formatters';

const DEFAULT_PAGE_SIZE = 50;

const columns = [
  {
    accessor: 'transactionDate',
    Header: 'Date',
    Cell: rowData => formatDateddMMyyyy(rowData.row.original.transactionDate)
  },
  {
    accessor: 'reference',
    Header: 'Reference'
  },
  {
    accessor: 'narrative',
    Header: 'Narrative'
  },
  {
    accessor: 'source',
    Header: 'Source'
  },
  {
    accessor: 'amount',
    Header: 'Amount',
    Cell: rowData => formatCurrency(rowData.row.original.amount)
  }
];

const DepartmentSpendingTransactionsModal = ({
  show,
  onClose,
  department,
  financialYearId,
  cell
}) => {
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [paging, setPaging] = useState({
    pageNum: 1,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [isLoading, setIsLoading] = useState(false);

  const loadTransactions = async rq => {
    setIsLoading(true);
    try {
      const res = await getDepartmentSpendingTransactions({
        department,
        accountNumber: cell?.account?.code,
        financialYearId,
        periodId: cell?.periodId,
        pageNum: rq.pageNum,
        pageSize: rq.pageSize
      });
      setTransactions(res?.data?.items || []);
      setTotalCount(res?.data?.totalCount || 0);
      setPaging(rq);
    } catch (error) {
      console.log('AXIOS ERROR', error);
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !cell?.account?.code) return;
    loadTransactions({ pageNum: 1, pageSize: DEFAULT_PAGE_SIZE });
  }, [show, cell, department, financialYearId]);

  const gotoPage = index => {
    const nextPageNum = index + 1;
    if (nextPageNum <= 0) return;
    loadTransactions({ ...paging, pageNum: nextPageNum });
  };

  const nextPage = () => {
    const nextPageNum = paging.pageNum + 1;
    if (nextPageNum > Math.ceil(totalCount / paging.pageSize)) return;
    loadTransactions({ ...paging, pageNum: nextPageNum });
  };

  const previousPage = () => {
    const previousPageNum = paging.pageNum - 1;
    if (previousPageNum <= 0) return;
    loadTransactions({ ...paging, pageNum: previousPageNum });
  };

  const periodContextLabel = cell?.periodId
    ? cell?.periodLabel
    : 'Total (whole financial year)';

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {cell?.account?.code} - {cell?.account?.name}
          <div className="fs--1 text-muted">{periodContextLabel}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <PartialTable
          data={transactions}
          columns={columns}
          paging={paging}
          total={totalCount}
          nextPage={nextPage}
          previousPage={previousPage}
          gotoPage={gotoPage}
          loading={isLoading}
        />
      </Modal.Body>
    </Modal>
  );
};

DepartmentSpendingTransactionsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  department: PropTypes.string,
  financialYearId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cell: PropTypes.shape({
    account: PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string
    }),
    periodId: PropTypes.number,
    periodLabel: PropTypes.string
  })
};

export default DepartmentSpendingTransactionsModal;
